<script setup lang="ts">
import { computed, inject } from "vue"
import { useRoute } from "vue-router"
import { useQuery } from "@tanstack/vue-query"
import { Download } from "lucide-vue-next"
import { useI18n } from "vue-i18n"

import { getLocalizedAbstract, listAuthors } from "@/lib/article.ts"
import { CurrentUser, getPermissions } from "@/lib/user.ts"
import { doGetRequest, generateDownloadLink } from "@/lib/utils.ts"
import { doLogin } from "@/lib/router_utils.ts"

import { LoaderCircle } from "lucide-vue-next"

import { getPublication, getPublicationAttachments, getPublicationArticles } from "@/models/publication"

import Button from "@/components/ui/button/Button.vue"
import Separator from "@/components/ui/separator/Separator.vue"
import ListBox from "@/components/ui-custom/list-box/index.vue"
import AttachmentPreview from "@/components/ui-custom/attachment-preview/AttachmentPreview.vue"
import Navbar from "@/components/ui-custom/navbar/Navbar.vue"

const route = useRoute()
const publicationId = route.params.id as string

const currentUser = inject("currentUser") as CurrentUser
const permissions = getPermissions("studimusicali")

const { t } = useI18n()

const { data: publication } = useQuery({
  queryKey: ["publications", route.params.id],
  queryFn: ({ signal }) => getPublication({ signal: signal, id: publicationId }),
  staleTime: 10 * 60 * 1000 // 10 minutes
})

const {
  data: attachments,
  isLoading,
  isError
} = useQuery({
  queryKey: ["publications", route.params.id, "attachments"],
  queryFn: ({ signal }) =>
    getPublicationAttachments({
      signal: signal,
      documentId: documentId.value,
      publicationId: publicationId
    }),
  enabled: computed(() => !!publication.value && !!currentUser.user),
  staleTime: 10 * 60 * 1000 // 10 minutes
})

const { data: articles } = useQuery({
  queryKey: ["publications", publicationId, "articles"],
  queryFn: ({ signal }) => getPublicationArticles({ signal: signal, id: publicationId }),
  staleTime: 10 * 60 * 1000 // 10 minutes
})

const documentId = computed(() => {
  return publication?.value?.document_id
})

const downloadUrl = computed(() => {
  if (!documentId.value) return ""
  if (!publication?.value?.blob_id) return ""

  let filename = `${t("commons.publication")}_${publication.value?.year}_${publication.value?.number}.pdf`

  return generateDownloadLink(documentId.value, publication?.value?.blob_id, filename)
})

const updateStats = (): void => {
  doGetRequest("/api/update_stats/download", {
    params: {
      type: "publication",
      publication_id: publicationId
    }
  })
}

const handleDownload = (): void => {
  updateStats()
}

const reloadPage = () => {
  window.location.reload()
}
</script>

<template>
  <navbar />

  <div class="container flex flex-col lg:flex-row" v-if="publication">
    <div class="flex flex-1 flex-col gap-4 pb-4 pr-4">
      <div class="title">{{ $t("publication.title") }}</div>

      <div class="flex justify-between">
        <div class="text-primary">{{ $t("publication.issn") }}:</div>
        <div>{{ publication.issn }}</div>
      </div>

      <div class="flex justify-between">
        <div class="text-primary">{{ $t("commons.year") }}:</div>
        <div>{{ publication.year }}</div>
      </div>

      <div class="flex justify-between">
        <div class="text-primary">{{ $t("commons.number") }}:</div>
        <div>{{ publication.number }}</div>
      </div>

      <div class="flex justify-between">
        <div class="text-primary">{{ $t("publication.pages") }}:</div>
        <div>{{ publication.total_pages }}</div>
      </div>

      <template v-if="currentUser.user && permissions.includes('download')">
        <Separator />

        <div class="flex-0 flex cursor-pointer text-primary hover:text-foreground">
          <a :href="downloadUrl" @click="handleDownload" class="flex w-full flex-row">
            <span class="flex-1">Download PDF</span>

            <span class="ml-auto">
              <Download />
            </span>
          </a>
        </div>
      </template>

      <Separator />

      <div class="flex-0 flex h-[500px] flex-col gap-1 overflow-y-auto bg-muted p-1" v-if="articles">
        <list-box
          :url="{ name: 'show_article_path', params: { id: article.id } }"
          :key="`article-${article.id}`"
          v-for="article in articles">
          <template #title>
            {{ article.title }}
          </template>

          <template #default>
            <div class="flex-0 text-gray-600" v-if="article?.authors?.length">
              {{ $t("commons.by") }} <span class="font-semibold">{{ listAuthors(article.authors) }}</span>
            </div>

            <div class="line-clamp-2 flex-1" v-if="getLocalizedAbstract(article)">
              {{ getLocalizedAbstract(article) }}
            </div>
          </template>
        </list-box>
      </div>
    </div>

    <div class="flex max-w-[500px] flex-1 flex-col p-4">
      <div class="relative flex min-h-[740px] border border-gray-400">
        <div class="flex h-full w-full flex-col items-center justify-center" v-if="isLoading">
          <LoaderCircle class="h-20 animate-spin" />
        </div>

        <template v-else-if="currentUser.user && permissions.includes('read') && !isError">
          <AttachmentPreview :slides="attachments" />
        </template>

        <div class="flex h-full w-full flex-col items-center justify-center gap-2" v-else-if="isError">
          <div>Si è verificato un errore durante il caricamento.</div>
          <Button type="button" @click="reloadPage">Ricarica</Button>
        </div>

        <div class="w-full" v-else>
          <div class="flex h-full flex-1 flex-col items-center justify-center gap-4">
            <template v-if="!currentUser.user || isError">
              <div>{{ $t("commons.unauthorized") }}</div>

              <Button @click="doLogin()">{{ $t("commons.login") }}</Button>
            </template>

            <div v-else>
              {{ $t("commons.missing_permissions") }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.pswp {
  z-index: 10;
}
</style>
